import React, { Suspense, useState } from 'react'
import { useSearchParams, useLocation } from 'react-router'
import {
  ActionIcon,
  Alert,
  Box,
  Drawer,
  LoadingOverlay,
  Pagination,
  Stack,
} from '@mantine/core'
import { useMediaQuery, usePagination, useToggle } from '@mantine/hooks'
import {
  IconAdjustmentsHorizontal,
  IconInfoCircle,
  IconX,
  IconZoom,
} from '@tabler/icons-react'
import { ApiNewsFilterType, useFeaturedNews, useNews } from './api'
import NewsCard from './NewsCard'
import NewsFilters, { NewsFilterType } from './NewsFilters'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import FeaturedNewsComponent from './FeaturedNews'
import { Typography } from '../common/typography'
import classes from './News.module.css'
import { MostRead } from './MostRead'
import Tabs from '../stories/Tabs/Tabs'
import { useCompetitionId } from '../common/hooks'
import { Input } from '../common/components/input/StyledTextInput'

interface NewsSearchInputProps {
  onSearch: (value: string | undefined) => void
  className?: string
}

function NewsSearchInput({ onSearch, className }: NewsSearchInputProps) {
  const [value, setValue] = useState('')

  return (
    <Input
      placeholder="Search"
      rightSection={
        value !== '' ? (
          <IconX
            width={14}
            height={14}
            className={classes.searchIcon}
            onClick={() => {
              setValue('')
              onSearch(undefined)
            }}
          />
        ) : (
          <IconZoom width={14} height={14} className={classes.searchIcon} />
        )
      }
      rightSectionPointerEvents="auto"
      size="xs"
      classNames={{
        input: className || classes.search,
      }}
      value={value}
      onChange={(event) => setValue(event.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const value = e.currentTarget.value.trim()
          onSearch(value || undefined)
        }
      }}
    />
  )
}

export default function News() {
  const location = useLocation()
  const category = (location.state as { category?: string })?.category

  const [search, setSearch] = React.useState<string | undefined>(undefined)
  const [filter, setFilter] = React.useState<NewsFilterType>(() => {
    if (category === 'announcements') return { is_announcement: true }
    if (category === 'news') return { is_announcement: false }
    return {}
  })
  const [filterDrawer, toggleFilterDrawer] = useToggle([false, true])
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const competition_id = useCompetitionId()

  const tabs = [
    {
      value: 'all',
      label: 'All',
    },
    {
      value: 'news',
      label: 'News',
    },
    {
      value: 'announcements',
      label: 'Announcements',
    },
  ]

  const newsCategory =
    filter.is_announcement == null
      ? 'all'
      : filter.is_announcement
        ? 'announcements'
        : 'news'

  const { data: featuredNews } = useFeaturedNews({
    competition_id: competition_id,
  })

  if (isDesktop) {
    return (
      <div className={classes.container}>
        <Meta title="News" />
        <Header title="News" />
        <div className={classes.newsContainer}>
          {featuredNews && (
            <div className={classes.rowWrapper}>
              <FeaturedNewsComponent news={featuredNews} />
              <MostRead title="Most Read" />
            </div>
          )}

          <div className={classes.rowWrapper}>
            <div className={classes.latest_news_container}>
              <div className={classes.newsFeedHeader}>
                <Typography
                  level={2}
                  fw={500}
                  lh="125"
                  fc={{ light: 'black', dark: 'gray-3' }}
                >
                  {search ? `Searched for: "${search}"` : 'Latest News'}
                </Typography>

                <Tabs
                  items={tabs}
                  value={newsCategory}
                  onChange={(cat) => {
                    setFilter({
                      ...filter,
                      is_announcement:
                        cat === 'announcements'
                          ? true
                          : cat === 'news'
                            ? false
                            : undefined,
                    })
                  }}
                  isSmall
                />
              </div>
              <LoadingNewsList filter={{ ...filter, search }} />
            </div>

            <div className={classes.filtersContainer}>
              <NewsSearchInput
                onSearch={(value) => {
                  setSearch(value)
                }}
              />
              <div className={classes.newsFilters}>
                <Typography
                  fw={500}
                  fc={{ light: 'black', dark: 'gray-3' }}
                  lh="125"
                >
                  Filters
                </Typography>
                <NewsFilters onChange={setFilter} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <Meta title="News" />
      <Header title="News" />
      <div className={classes.newsContent}>
        {featuredNews && <FeaturedNewsComponent news={featuredNews} />}
        <div className={classes.latestNewsContainer}>
          <div className={classes.latestNewsSearch}>
            <Typography
              level={2}
              fs="xl"
              fw={500}
              lh="125"
              fc={{ light: 'black', dark: 'gray-2' }}
            >
              {search ? `Searched for: "${search}"` : 'Latest News'}
            </Typography>
            <NewsSearchInput
              onSearch={(value) => {
                setSearch(value)
              }}
            />
          </div>
          <ActionIcon
            variant="outline"
            className={classes.filterActionIcon}
            onClick={() => toggleFilterDrawer()}
          >
            <IconAdjustmentsHorizontal className={classes.filterIcon} />
          </ActionIcon>
        </div>
        <LoadingNewsList filter={{ ...filter, search }} />
      </div>
      <Drawer
        opened={filterDrawer}
        onClose={() => toggleFilterDrawer()}
        position="bottom"
        size="xs"
        title="Filters"
        zIndex={302}
        classNames={{
          header: classes.drawerHeader,
          content: classes.drawerContent,
        }}
      >
        <Stack>
          <NewsFilters onChange={setFilter} announcementFilter />
        </Stack>
      </Drawer>
    </div>
  )
}

const shallowCompare = (
  // eslint-disable-next-line
  obj1: { [k: string]: any },
  // eslint-disable-next-line
  obj2: { [k: string]: any }
) =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.keys(obj1).every((key) => obj1[key] === obj2[key])

function LoadingNewsList({ filter }: { filter: Partial<ApiNewsFilterType> }) {
  const deferredFilter = React.useDeferredValue(filter)
  const isStale = !shallowCompare(deferredFilter, filter)

  return (
    <Box
      pos="relative"
      style={{ minHeight: '300px', opacity: isStale ? 0.5 : 1 }}
    >
      <Suspense
        fallback={<LoadingOverlay visible overlayProps={{ opacity: 0 }} />}
      >
        <LoadingOverlay visible={isStale} overlayProps={{ opacity: 0 }} />
        <NewsList filter={deferredFilter} />
      </Suspense>
    </Box>
  )
}

function NewsList({ filter }: { filter: Partial<ApiNewsFilterType> }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const pageSize = 12
  const competition_id = useCompetitionId()

  const { data: news } = useNews({
    page: currentPage,
    page_size: pageSize,
    competition_id: competition_id,
    ...filter,
  })

  const totalPages = Math.ceil(news.total / pageSize)

  const pagination = usePagination({
    total: totalPages,
    initialPage: currentPage,
    siblings: 1,
  })

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() })
    pagination.setPage(page)
  }

  let newsList: React.ReactNode | null = null

  if (news.news.length === 0) {
    const message = filter.search
      ? 'No news found for your search'
      : 'No news available'
    newsList = (
      <Stack maw={1000} w="full" h={200} align="center" justify="center">
        <Alert
          classNames={{ root: classes.alertRoot, icon: classes.alertIcon }}
          icon={<IconInfoCircle />}
        >
          {message}
        </Alert>
      </Stack>
    )
  } else {
    newsList = (
      <div className={classes.news}>
        {news.news.map((item) => (
          <NewsCard key={item.news_id} news={item} />
        ))}
      </div>
    )
  }

  return (
    <>
      {newsList}
      <div className={classes.pagination}>
        <Pagination
          classNames={{ control: classes.control }}
          value={currentPage}
          onChange={handlePageChange}
          total={totalPages}
        />
      </div>
    </>
  )
}
